<template>
    <div class="horizontal-bars">
        <div v-for="item in rows" :key="item.label" class="hbar-row">
            <span class="hbar-label" :title="item.label">{{ item.label }}</span>
            <div class="hbar-track">
                <div class="hbar-fill" :style="{ width: `${item.width}%`, background: item.color }">
                    <span class="hbar-value">{{ item.text }}</span>
                </div>
            </div>
            <span v-if="showPercent" class="hbar-percent">{{ item.percent }}%</span>
        </div>
        <span v-if="rows.length === 0" class="hbar-empty">{{ emptyText || $t('common.without_data') }}</span>
    </div>
</template>

<script>
const MIN_WIDTH = 12

export default {
    name: "HorizontalBars",
    props: {
        items: {
            type: Array,
            default: () => [],
        },
        color: {
            type: String,
            default: '',
        },
        showPercent: {
            type: Boolean,
            default: false,
        },
        valueFormatter: {
            type: Function,
            default: null,
        },
        emptyText: {
            type: String,
            default: '',
        },
    },
    computed: {
        total(vm = this) {
            return vm.items.reduce((sum, item) => sum + (Number(item.value) || 0), 0)
        },
        max(vm = this) {
            return vm.items.reduce((top, item) => Math.max(top, Number(item.value) || 0), 0)
        },
        rows(vm = this) {
            const fallback = vm.color || vm.$vuetify.theme.currentTheme.primary
            return vm.items
                .filter(item => (Number(item.value) || 0) > 0)
                .map(item => {
                    const value = Number(item.value) || 0
                    return {
                        label: item.label,
                        color: item.color || fallback,
                        text: item.text || (vm.valueFormatter ? vm.valueFormatter(value) : value),
                        width: vm.max ? Math.max(MIN_WIDTH, Math.round(value / vm.max * 100)) : 0,
                        percent: vm.total ? Math.round(value / vm.total * 100) : 0,
                    }
                })
        },
    },
}
</script>

<style scoped>
.horizontal-bars {
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    gap: 7px;
    flex: 1 1 auto;
    min-height: 0;
}

.hbar-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.hbar-label {
    width: 78px;
    flex-shrink: 0;
    text-align: right;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--v-txtMuted-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.hbar-track {
    flex: 1 1 auto;
    min-width: 0;
    height: 26px;
    border-radius: 6px;
    background: var(--v-surface2-base);
    overflow: hidden;
}

.hbar-fill {
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 9px;
    border-radius: 6px;
    transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.hbar-value {
    font-size: var(--fs-caption);
    font-weight: 800;
    color: #0C0E12;
    white-space: nowrap;
}

.hbar-percent {
    width: 30px;
    flex-shrink: 0;
    font-size: 10px;
    font-weight: 700;
    text-align: right;
    color: var(--v-txtMuted-base);
}

.hbar-empty {
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}
</style>
